import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Scan,
    Tag,
    Layers,
    Scale,
    Heart,
    MessageCircle,
    FileText,
    Package,
    X,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    RotateCcw
} from 'lucide-react';
import ProductCard from '../ProductCard';
import { auth, db } from '../../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useQuote } from '../Quote/QuoteContext';

export default function ProductDetails({ handleAddToCart, searchQuery, setSearchQuery, myList, handleToggleMyList, cartCount, onCartClick, onStoreLocationsClick, onAuthClick }) {
    const { productId } = useParams();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);

    const activeProduct = product || {};
    const minimum_order = activeProduct.minimum_order || 1;

    // Product quantity state (defaults to 1)
    const [specQty, setSpecQty] = useState(minimum_order);
    const { openQuote } = useQuote();

    const [schemeId, setSchemeId] = useState(null);
    const [tiers, setTiers] = useState([]);
    const [selectedTier, setSelectedTier] = useState(null);
    const [unitPrice, setUnitPrice] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [zoomScale, setZoomScale] = useState(1);

    const handleZoomIn  = () => setZoomScale(s => Math.min(4, +(s + 0.25).toFixed(2)));
    const handleZoomOut = () => setZoomScale(s => Math.max(0.5, +(s - 0.25).toFixed(2)));
    const handleZoomReset = () => setZoomScale(1);

    const findActiveTier = (qty, currentTiers) => {
        const activeTiers = currentTiers || tiers;
        if (activeTiers.length === 0) return null;
        const ascTiers = [...activeTiers].sort((a, b) => {
            const qa = Number(a.min || a.min_qty || a.minQty || 0);
            const qb = Number(b.min || b.min_qty || b.minQty || 0);
            return qa - qb;
        });
        const matched = [...ascTiers].reverse().find(t => qty >= Number(t.min || t.min_qty || t.minQty || 0));
        return matched || ascTiers[0];
    };

    const getPriceForQty = (qty, currentTiers) => {
        const tier = findActiveTier(qty, currentTiers);
        return tier ? Number(tier.price || 0) : (activeProduct.price || activeProduct.distributorPrice || 0);
    };

    useEffect(() => {
        const fetchProductData = async () => {
            setLoading(true);
            try {
                let currentProduct = null;
                const prodDocRef = doc(db, "products", productId);
                const prodSnap = await getDoc(prodDocRef);
                if (prodSnap.exists()) {
                    const data = prodSnap.data();
                    const displayCode = data.productCodeLetters && data.productCode
                        ? `${data.productCodeLetters}${data.productCode}`
                        : (data.code || '');
                    currentProduct = {
                        id: prodSnap.id,
                        title: data.productName || data.title || '',
                        code: displayCode,
                        images: Array.isArray(data.images) && data.images.length > 0
                            ? data.images
                            : (data.image ? [data.image] : (data.thumbnail ? [data.thumbnail] : [])),
                        brand: data.brandName || data.brand || '',
                        type: data.type_name || data.type || '',
                        pack: data.prices || data.pack || data.unitsOfMeasurement || '',
                        minimum_order: data.minimum_order || 1,
                        ...data
                    };
                    // Fallback: images array → single image → thumbnail → placeholder
                    const firstImage =
                        (Array.isArray(data.images) && data.images.length > 0 ? data.images[0] : null)
                        || data.image
                        || data.thumbnail
                        || '/product-placeholder.png';
                    setSelectedImage(firstImage);
                }

                setProduct(currentProduct || null);

                // Fetch related products
                const relatedRefs = currentProduct?.relatedProducts || [];
                if (relatedRefs.length > 0) {
                    const relatedDocs = await Promise.all(
                        relatedRefs.map(({ id: relId }) => getDoc(doc(db, 'products', relId)))
                    );
                    const fetchedRelated = relatedDocs
                        .filter(snap => snap.exists())
                        .map(snap => {
                            const d = snap.data();
                            const displayCode = d.productCodeLetters && d.productCode
                                ? `${d.productCodeLetters}${d.productCode}`
                                : (d.code || '');
                            return {
                                id: snap.id,
                                title: d.productName || d.title || '',
                                code: displayCode,
                                image: d.image || (d.images?.[0]) || '/product-placeholder.png',
                                brand: d.brandName || d.brand || '',
                                type: d.type_name || d.type || '',
                                pack: d.prices || d.pack || d.unitsOfMeasurement || '',
                                minimum_order: d.minimum_order || 1,
                                ...d
                            };
                        });
                    setRelatedProducts(fetchedRelated);
                } else {
                    setRelatedProducts([]);
                }

                // Fetch user scheme & pricing
                const user = auth.currentUser;
                if (user && currentProduct) {
                    let userDocRef = doc(db, "users", user.uid);
                    let userSnap = await getDoc(userDocRef);
                    if (!userSnap.exists()) {
                        userDocRef = doc(db, "users", user.email.toLowerCase());
                        userSnap = await getDoc(userDocRef);
                    }
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        const sId = userData.scheme_id || userData.schema_id || null;
                        setSchemeId(sId);

                        // If user has a scheme, get the tiers
                        if (sId) {
                            // We fetch the latest product document to get pricing tiers
                            const prodDocRef = doc(db, "products", productId);
                            const prodSnap = await getDoc(prodDocRef);
                            if (prodSnap.exists()) {
                                const prodData = prodSnap.data();
                                const pricingList = prodData.pricing || [];
                                const schemeIdx = prodData.schemeIds && Array.isArray(prodData.schemeIds) ? prodData.schemeIds.findIndex(id => String(id) === String(sId)) : -1;
                                const targetPricing = pricingList.find(p => sId && String(p.scheme_id || p.schema_id || p.schemeId || '') === String(sId)) || (schemeIdx !== -1 ? pricingList[schemeIdx] : null);
                                if (targetPricing && targetPricing.tiers) {
                                    const sortedTiers = [...targetPricing.tiers].sort((a, b) => {
                                        const qa = Number(a.min || a.minQty || 0);
                                        const qb = Number(b.min || b.minQty || 0);
                                        return qa - qb;
                                    });
                                    setTiers(sortedTiers);

                                    if (sortedTiers.length > 0) {
                                        setSelectedTier(sortedTiers[0]);
                                        const initialQty = Number(sortedTiers[0].min || sortedTiers[0].minQty || currentProduct.minimum_order || 1);
                                        setSpecQty(initialQty);
                                        setUnitPrice(Number(sortedTiers[0].price || 0));
                                    } else {
                                        setTiers([]);
                                        setSelectedTier(null);
                                        setUnitPrice(currentProduct.price || 0);
                                    }
                                } else {
                                    setTiers([]);
                                    setSelectedTier(null);
                                    setUnitPrice(currentProduct.price || 0);
                                }
                            } else {
                                setTiers([]);
                                setSelectedTier(null);
                                setUnitPrice(currentProduct.price || 0);
                            }
                        } else {
                            setTiers([]);
                            setSelectedTier(null);
                            setUnitPrice(currentProduct.price || 0);
                        }
                    } else {
                        setSchemeId(null);
                        setTiers([]);
                        setSelectedTier(null);
                        setUnitPrice(currentProduct.price || 0);
                    }
                } else {
                    setSchemeId(null);
                    setTiers([]);
                    setSelectedTier(null);
                    setUnitPrice(currentProduct ? (currentProduct.price || 0) : 0);
                }
            } catch (err) {
                console.error("Error fetching product data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchProductData();

        // Listen to auth state changes to re-fetch scheme when logging in/out
        const unsubscribe = auth.onAuthStateChanged((user) => {
            fetchProductData();
        });

        return () => unsubscribe();
    }, [productId]);

    const handleTierSelect = (tier) => {
        setSelectedTier(tier);
        const qty = Number(tier.min || tier.min_qty || tier.minQty || minimum_order);
        setSpecQty(qty);
        setUnitPrice(Number(tier.price || 0));
    };

    const handleSpecQtyChange = (e) => {
        const val = e.target.value;
        if (val === '') {
            setSpecQty('');
        } else {
            const parsed = parseInt(val, 10);
            if (!isNaN(parsed) && parsed >= minimum_order) {
                setSpecQty(parsed);
                setUnitPrice(getPriceForQty(parsed));
                setSelectedTier(findActiveTier(parsed));
            }
        }
    };

    const handleSpecQtyBlur = () => {
        if (specQty === '' || specQty < minimum_order) {
            setSpecQty(minimum_order);
            setUnitPrice(getPriceForQty(minimum_order));
            setSelectedTier(findActiveTier(minimum_order));
            toast.error(`Minimum order quantity is ${minimum_order}`);
        }
    };

    const handleAddToCartClick = () => {
        const qty = Number(specQty) || minimum_order;
        if (handleAddToCart) {
            handleAddToCart(activeProduct, qty);
        }
    };

    const openQuotePopup = () => {
        if (!auth.currentUser) {
            window.dispatchEvent(new Event('open-auth'));
            return;
        }
        openQuote(activeProduct);
    };

    const subtotalText = typeof unitPrice === 'number' ? unitPrice.toFixed(2) : '0.00';
    const bulkTotalText = typeof unitPrice === 'number' ? (unitPrice * (Number(specQty) || 0)).toFixed(2) : '0.00';
    const hasPricingForScheme = typeof unitPrice === 'number' && unitPrice > 0 && schemeId !== undefined;
    const isOutOfStock = activeProduct && activeProduct.stock !== undefined && activeProduct.stock < (activeProduct.minimum_order || 1);


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen" style={{ background: '#F8F7F3' }}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2C3E2B]"></div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen" style={{ background: '#F8F7F3' }}>
                <h3 className="text-xl font-bold text-[#2C3E2B]">Product not found</h3>
                <p className="text-slate-400 mt-2">We couldn't load the product you requested.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-20 pt-8" style={{ background: '#F8F7F3' }}>
            <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">

                {/* Main Product Columns */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-6">

                    {/* ═══ Left Side: Image, Gallery & Title ═══ */}
                    <div className="lg:col-span-7 flex flex-col gap-4 text-left">
                        <div className="flex flex-row gap-3 w-full">
                            {/* Vertical Thumbnail Strip — left of main image */}
                            {Array.isArray(activeProduct.images) && activeProduct.images.length > 1 && (
                                <div className="flex flex-col gap-2.5 overflow-y-auto max-h-[460px] py-1 pr-0.5 flex-shrink-0 overscroll-contain">
                                    {activeProduct.images.map((imgUrl, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => { setSelectedImage(imgUrl); setPreviewIndex(idx); }}
                                            className={`w-[60px] h-[60px] rounded-xl border-2 p-1 flex items-center justify-center bg-slate-50 cursor-pointer overflow-hidden flex-shrink-0 transition-all ${selectedImage === imgUrl
                                                ? 'border-[#2C3E2B] shadow-md'
                                                : 'border-slate-200 hover:border-slate-400'
                                                }`}
                                            aria-label={`View image ${idx + 1}`}
                                        >
                                            <img
                                                src={imgUrl}
                                                alt={`${activeProduct.title} view ${idx + 1}`}
                                                className="max-h-full max-w-full object-contain mix-blend-multiply"
                                                onError={(e) => { e.target.onerror = null; e.target.src = '/product-placeholder.png'; }}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Main Image Column */}
                            <div className="flex flex-col flex-1 min-w-0">
                                {/* Main Image with bottom overlay */}
                                <div className="relative border-2 border-slate-200 rounded-[28px] bg-slate-50 flex items-center justify-center p-3 pb-16 h-[380px] md:h-[460px] overflow-hidden shadow-xs w-full group">
                                    <img
                                        src={selectedImage || '/product-placeholder.png'}
                                        alt={activeProduct.title}
                                        className="max-h-[78%] max-w-[85%] object-contain mix-blend-multiply transition-all duration-300 group-hover:scale-105 cursor-zoom-in"
                                        onError={(e) => { e.target.onerror = null; e.target.src = '/product-placeholder.png'; }}
                                        onClick={() => setPreviewOpen(true)}
                                    />

                                    {/* ─── Image Preview Lightbox with Zoom ─── */}
                                    {previewOpen && (() => {
                                        const imgs = Array.isArray(activeProduct.images) && activeProduct.images.length > 0
                                            ? activeProduct.images
                                            : [selectedImage || '/product-placeholder.png'];
                                        const canNav = imgs.length > 1;
                                        return (
                                            <div
                                                className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
                                                style={{ background: 'rgba(10,10,10,0.93)', backdropFilter: 'blur(6px)' }}
                                                onClick={() => { setPreviewOpen(false); setZoomScale(1); }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') { setPreviewOpen(false); setZoomScale(1); }
                                                    if (e.key === 'ArrowRight' && canNav) { setPreviewIndex(i => (i + 1) % imgs.length); setZoomScale(1); }
                                                    if (e.key === 'ArrowLeft' && canNav) { setPreviewIndex(i => (i - 1 + imgs.length) % imgs.length); setZoomScale(1); }
                                                    if (e.key === '+' || e.key === '=') handleZoomIn();
                                                    if (e.key === '-') handleZoomOut();
                                                    if (e.key === '0') handleZoomReset();
                                                }}
                                                onWheel={(e) => { e.preventDefault(); e.stopPropagation(); e.deltaY < 0 ? handleZoomIn() : handleZoomOut(); }}
                                                tabIndex={0}
                                                ref={el => el && el.focus()}
                                            >
                                                {/* Top bar: counter + zoom controls + close */}
                                                <div
                                                    className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-3 z-20"
                                                    style={{ background: 'rgba(0,0,0,0.45)' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {/* Counter */}
                                                    <div className="text-white/70 text-xs font-bold select-none">
                                                        {canNav ? `${previewIndex + 1} / ${imgs.length}` : activeProduct.title}
                                                    </div>

                                                    {/* Zoom controls */}
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={handleZoomOut}
                                                            disabled={zoomScale <= 0.5}
                                                            className="bg-white/10 hover:bg-white/25 disabled:opacity-30 text-white rounded-lg p-1.5 transition-all cursor-pointer border-none"
                                                            aria-label="Zoom out"
                                                            title="Zoom out (−)"
                                                        >
                                                            <ZoomOut className="w-4 h-4" />
                                                        </button>
                                                        <span
                                                            className="text-white/80 text-xs font-extrabold w-11 text-center select-none tabular-nums cursor-pointer"
                                                            onClick={handleZoomReset}
                                                            title="Click to reset zoom"
                                                        >
                                                            {Math.round(zoomScale * 100)}%
                                                        </span>
                                                        <button
                                                            onClick={handleZoomIn}
                                                            disabled={zoomScale >= 4}
                                                            className="bg-white/10 hover:bg-white/25 disabled:opacity-30 text-white rounded-lg p-1.5 transition-all cursor-pointer border-none"
                                                            aria-label="Zoom in"
                                                            title="Zoom in (+)"
                                                        >
                                                            <ZoomIn className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={handleZoomReset}
                                                            className="bg-white/10 hover:bg-white/25 text-white rounded-lg p-1.5 transition-all cursor-pointer border-none ml-1"
                                                            aria-label="Reset zoom"
                                                            title="Reset (0)"
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>

                                                    {/* Close */}
                                                    <button
                                                        onClick={() => { setPreviewOpen(false); setZoomScale(1); }}
                                                        className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-all cursor-pointer border-none"
                                                        aria-label="Close preview"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                {/* Prev */}
                                                {canNav && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setPreviewIndex(i => (i - 1 + imgs.length) % imgs.length); setZoomScale(1); }}
                                                        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-all cursor-pointer border-none z-10"
                                                        aria-label="Previous image"
                                                    >
                                                        <ChevronLeft className="w-6 h-6" />
                                                    </button>
                                                )}

                                                {/* Zoomable Image */}
                                                <div
                                                    className="flex items-center justify-center w-full h-full pt-14 pb-10"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ overflow: zoomScale > 1 ? 'auto' : 'hidden' }}
                                                >
                                                    <img
                                                        src={imgs[previewIndex]}
                                                        alt={`${activeProduct.title} - image ${previewIndex + 1}`}
                                                        className="object-contain rounded-xl shadow-2xl select-none transition-transform duration-200"
                                                        style={{
                                                            maxHeight: zoomScale === 1 ? '80vh' : 'none',
                                                            maxWidth: zoomScale === 1 ? '85vw' : 'none',
                                                            transform: `scale(${zoomScale})`,
                                                            transformOrigin: 'center center',
                                                            cursor: zoomScale > 1 ? 'move' : 'zoom-in',
                                                        }}
                                                        onError={(e) => { e.target.onerror = null; e.target.src = '/product-placeholder.png'; }}
                                                        onClick={(e) => { e.stopPropagation(); zoomScale < 4 ? handleZoomIn() : handleZoomReset(); }}
                                                    />
                                                </div>

                                                {/* Next */}
                                                {canNav && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setPreviewIndex(i => (i + 1) % imgs.length); setZoomScale(1); }}
                                                        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-all cursor-pointer border-none z-10"
                                                        aria-label="Next image"
                                                    >
                                                        <ChevronRight className="w-6 h-6" />
                                                    </button>
                                                )}

                                                {/* Dot navigation + zoom hint */}
                                                <div
                                                    className="absolute bottom-0 inset-x-0 flex flex-col items-center gap-2 py-3 z-10"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {canNav && (
                                                        <div className="flex gap-2">
                                                            {imgs.map((_, i) => (
                                                                <button
                                                                    key={i}
                                                                    onClick={() => { setPreviewIndex(i); setZoomScale(1); }}
                                                                    className={`w-2 h-2 rounded-full border-none cursor-pointer transition-all duration-200 ${
                                                                        i === previewIndex ? 'bg-white scale-125' : 'bg-white/35 hover:bg-white/65'
                                                                    }`}
                                                                    aria-label={`Go to image ${i + 1}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                    <p className="text-white/30 text-[10px] select-none">
                                                        Scroll to zoom · Click image to zoom in · +/- keys
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* AR Circular badge */}
                                    <span className="absolute top-4 right-4 bg-white/95 text-slate-800 font-extrabold text-[12px] px-4 py-4 rounded-full flex items-center justify-center shadow-md select-none border border-slate-100 cursor-pointer z-10">
                                        {activeProduct.code}
                                    </span>

                                    {/* Small Red Dot indicator */}
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-red-500 rounded-full shadow-xs z-10" />

                                    {/* Product Name Overlay Bar at bottom of image card */}
                                    <div className="absolute bottom-0 inset-x-0 bg-[#F3E9DC] border-t border-[#E2D4C3] py-3.5 px-5 text-[#2C3E2B] flex flex-col justify-end transition-all duration-300 z-10">
                                        <span className="text-[10px] font-black tracking-widest text-[#2C3E2B]/60 uppercase mb-0.5">
                                            Product Title
                                        </span>
                                        <h1 className="text-base sm:text-lg md:text-xl font-extrabold text-[#2C3E2B] leading-tight flex items-center gap-2 flex-wrap m-0">
                                            <span>{activeProduct.title}</span>
                                            {isOutOfStock && (
                                                <span className="text-[9px] font-black text-red-600 uppercase bg-red-50 border border-red-200 px-2 py-0.5 rounded-md flex-shrink-0">
                                                    Out of Stock
                                                </span>
                                            )}
                                        </h1>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ═══ Right Side: Unified Details & Action Card ═══ */}
                    <div className="lg:col-span-5 flex flex-col text-left">
                        <span className="text-[12px] font-black text-[#2C3E2B]/60 tracking-wider uppercase mb-3 block">
                            Product Details
                        </span>

                        {/* Unified Single Card Container */}
                        <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-xs flex flex-col">

                            {/* Section 1: Specs Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 bg-white">
                                {[
                                    { label: "Product Name", value: activeProduct.title || activeProduct.productName || "", icon: Package, fullWidth: true },
                                    { label: "Product Code", value: activeProduct.code || "", icon: Scan },
                                    { label: "Brand", value: activeProduct.brandName || activeProduct.brand || "", icon: Tag },
                                    { label: "Type", value: activeProduct.type_name || activeProduct.typeName || activeProduct.type || "", icon: Layers },
                                    { label: "Category", value: activeProduct.categoryName || activeProduct.category || "", icon: Layers },
                                    { label: "Subcategory", value: activeProduct.subCategoryName || activeProduct.subcategory || activeProduct.subCategory || "", icon: Layers },
                                    { label: "Unit of Measurement", value: activeProduct.pack || activeProduct.unitsOfMeasurement || "", icon: Scale },
                                    { label: "Description", value: activeProduct.description || activeProduct.productDescription || activeProduct.details || "", icon: FileText, fullWidth: true }
                                ].filter(spec => Boolean(spec.value)).map((spec, idx, arr) => {
                                    const IconComponent = spec.icon;
                                    const isFullWidth = spec.fullWidth;
                                    const isLeftColumn = idx % 2 !== 0 && !isFullWidth;

                                    return (
                                        <div
                                            key={idx}
                                            className={`flex items-start p-4 ${isFullWidth ? 'md:col-span-2' : isLeftColumn ? 'md:border-r border-slate-100' : ''
                                                } border-b border-slate-100`}
                                        >
                                            <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center text-slate-500 mr-3 flex-shrink-0 border border-slate-100">
                                                <IconComponent className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-[11px] text-slate-400 font-extrabold uppercase tracking-wider block">
                                                    {spec.label}
                                                </span>
                                                <div className={`text-[#2C3E2B] mt-0.5 whitespace-pre-line leading-snug ${spec.label === "Product Name"
                                                    ? "text-lg md:text-xl font-extrabold text-[#2C3E2B] tracking-tight flex items-center gap-2 flex-wrap"
                                                    : "text-[13px] font-semibold"
                                                    }`}>
                                                    <span>{spec.value}</span>
                                                    {spec.label === "Product Name" && isOutOfStock && (
                                                        <span className="text-[10px] font-black text-red-600 uppercase bg-red-50 border border-red-200 px-2 py-0.5 rounded-md flex-shrink-0">
                                                            Out of Stock
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Section 2: Pricing, Quantity & Action Buttons inside the SAME Card */}
                            <div className="p-5 bg-[#FCFAF7] border-t border-slate-200">
                                {hasPricingForScheme ? (
                                    <div className="flex flex-col gap-4">
                                        {/* Tier Pricing Buttons */}
                                        {tiers.length > 0 && (
                                            <div>
                                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2 block">Volume Pricing</span>
                                                <div className="flex gap-2 flex-wrap">
                                                    {tiers.map((tier, tIdx) => {
                                                        const minQty = Number(tier.min || 0);
                                                        const isSel = selectedTier === tier;
                                                        return (
                                                            <button
                                                                key={tIdx}
                                                                onClick={() => handleTierSelect(tier)}
                                                                className={`px-4 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer ${isSel
                                                                    ? 'bg-[#2C3E2B] text-white border-[#2C3E2B]'
                                                                    : 'bg-white text-[#2C3E2B] border-slate-200 hover:border-[#2C3E2B]'
                                                                    }`}
                                                            >
                                                                {minQty}+ Units (₹{Number(tier.price || 0).toFixed(2)})
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-6 flex-wrap">
                                            {/* Qty Selector */}
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Quantity</span>
                                                <div className="flex items-center bg-[#F1F3F0] rounded-xl px-2.5 py-1.5 border border-slate-200 justify-between gap-3 w-32 flex-shrink-0">
                                                    <button
                                                        onClick={() => {
                                                            setSpecQty(prev => {
                                                                const current = Number(prev) || minimum_order;
                                                                if (current <= minimum_order) {
                                                                    toast.error(`Minimum order quantity is ${minimum_order}`);
                                                                    return minimum_order;
                                                                }
                                                                const nextVal = current - 1;
                                                                setUnitPrice(getPriceForQty(nextVal));
                                                                setSelectedTier(findActiveTier(nextVal));
                                                                return nextVal;
                                                            });
                                                        }}
                                                        className="text-slate-400 hover:text-slate-700 font-bold text-lg px-2 bg-transparent border-none cursor-pointer select-none"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={specQty === '' ? '' : Math.max(Number(specQty) || 0, minimum_order)}
                                                        min={minimum_order}
                                                        onChange={handleSpecQtyChange}
                                                        onBlur={handleSpecQtyBlur}
                                                        className="w-10 text-center text-xs font-bold text-slate-700 bg-transparent border-none outline-none focus:outline-none p-0"
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            setSpecQty(prev => {
                                                                const nextVal = (Number(prev) || 0) + 1;
                                                                setUnitPrice(getPriceForQty(nextVal));
                                                                setSelectedTier(findActiveTier(nextVal));
                                                                return nextVal;
                                                            });
                                                        }}
                                                        className="text-slate-400 hover:text-slate-700 font-bold text-lg px-2 bg-transparent border-none cursor-pointer select-none"
                                                        aria-label="Increase quantity"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <span className="text-[10px] text-slate-500 font-semibold px-1">
                                                    Min Order: {minimum_order}
                                                </span>
                                            </div>

                                            {/* Price Summary */}
                                            <div className="flex flex-col">
                                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Price</span>
                                                <div className="text-lg font-extrabold text-[#2C3E2B]">
                                                    ₹{subtotalText} <span className="text-xs font-bold text-slate-400">each</span>
                                                </div>
                                                <div className="text-sm font-bold text-[#9E2A2B] mt-0.5">
                                                    Total: ₹{bulkTotalText}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-3 mt-2">
                                            <button
                                                onClick={handleAddToCartClick}
                                                className="flex-1 bg-[#2C3E2B] hover:bg-[#dedede] hover:text-[#2C3E2B] text-white font-extrabold text-[12px] py-3 rounded-2xl flex items-center justify-center gap-3 transition shadow-sm border-none cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                                            >
                                                <span>ADD TO CART</span>
                                            </button>
                                            <button
                                                onClick={() => handleToggleMyList(activeProduct.id || activeProduct.code || activeProduct.title)}
                                                className={`px-12 bg-[#F3E9DC] border border-slate-200 text-slate-700 font-extrabold text-[10px] py-3 rounded-2xl flex items-center justify-center gap-1.5 transition cursor-pointer hover:bg-transparent ${(myList.includes(activeProduct.id) || (activeProduct.code && myList.includes(activeProduct.code)) || (activeProduct.title && myList.includes(activeProduct.title))) ? 'text-[#9E2A2B]' : ''
                                                    }`}
                                            >
                                                <Heart className={`w-3.5 h-3.5 ${(myList.includes(activeProduct.id) || (activeProduct.code && myList.includes(activeProduct.code)) || (activeProduct.title && myList.includes(activeProduct.title))) ? 'fill-[#9E2A2B] text-[#9E2A2B]' : 'text-slate-500'
                                                    }`} />
                                                <span>{(myList.includes(activeProduct.id) || (activeProduct.code && myList.includes(activeProduct.code)) || (activeProduct.title && myList.includes(activeProduct.title))) ? 'SAVED' : 'WISHLIST'}</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-6 flex-wrap">
                                            {/* Qty Selector */}
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Quantity</span>
                                                <div className="flex items-center bg-[#F1F3F0] rounded-xl px-2.5 py-1.5 border border-slate-200 justify-between gap-3 w-32 flex-shrink-0">
                                                    <button
                                                        onClick={() => {
                                                            setSpecQty(prev => {
                                                                const current = Number(prev) || minimum_order;
                                                                if (current <= minimum_order) {
                                                                    toast.error(`Minimum order quantity is ${minimum_order}`);
                                                                    return minimum_order;
                                                                }
                                                                return current - minimum_order;
                                                            });
                                                        }}
                                                        className="text-slate-400 hover:text-slate-700 font-bold text-lg px-2 bg-transparent border-none cursor-pointer select-none"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        type="text"
                                                        value={specQty}
                                                        onChange={handleSpecQtyChange}
                                                        onBlur={handleSpecQtyBlur}
                                                        className="w-10 text-center text-xs font-bold text-slate-700 bg-transparent border-none outline-none focus:outline-none p-0"
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            setSpecQty(prev => (Number(prev) || 0) + minimum_order);
                                                        }}
                                                        className="text-slate-400 hover:text-slate-700 font-bold text-lg px-2 bg-transparent border-none cursor-pointer select-none"
                                                        aria-label="Increase quantity"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <span className="text-[10px] text-slate-500 font-semibold px-1">
                                                    Min Order: {minimum_order}
                                                </span>
                                            </div>

                                            {/* Price Summary */}
                                            <div className="flex flex-col">
                                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Price</span>
                                                <div className="text-base font-extrabold text-slate-500">
                                                    Price on Request
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons — mobile: Add+Wishlist on row 1, Request Quote on row 2 */}
                                        <div className="flex flex-col gap-2 mt-2">
                                            {/* Row 1: Add to Cart + Wishlist */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleAddToCartClick}
                                                    className="flex-1 bg-[#2C3E2B] hover:bg-[#dedede] hover:text-[#2C3E2B] text-white font-extrabold text-[12px] py-3 rounded-2xl flex items-center justify-center gap-2 transition shadow-sm border-none cursor-pointer hover:shadow-md hover:-translate-y-0.5"
                                                >
                                                    <span>ADD TO CART</span>
                                                </button>
                                                <button
                                                    onClick={() => handleToggleMyList(activeProduct.id || activeProduct.code || activeProduct.title)}
                                                    className={`flex-1 bg-[#F3E9DC] border border-slate-200 font-extrabold text-[10px] py-3 rounded-2xl flex items-center justify-center gap-1.5 transition cursor-pointer hover:bg-transparent ${(myList.includes(activeProduct.id) || (activeProduct.code && myList.includes(activeProduct.code)) || (activeProduct.title && myList.includes(activeProduct.title))) ? 'text-[#9E2A2B]' : 'text-slate-700'}`}
                                                >
                                                    <Heart className={`w-3.5 h-3.5 ${(myList.includes(activeProduct.id) || (activeProduct.code && myList.includes(activeProduct.code)) || (activeProduct.title && myList.includes(activeProduct.title))) ? 'fill-[#9E2A2B] text-[#9E2A2B]' : 'text-slate-500'}`} />
                                                    <span>{(myList.includes(activeProduct.id) || (activeProduct.code && myList.includes(activeProduct.code)) || (activeProduct.title && myList.includes(activeProduct.title))) ? 'SAVED' : 'WISHLIST'}</span>
                                                </button>
                                            </div>
                                            {/* Row 2: Request Quote */}
                                            <button
                                                onClick={openQuotePopup}
                                                className="w-full bg-[#C46A42] hover:bg-[#B75A37] text-white font-extrabold text-[12px] py-3 rounded-2xl flex items-center justify-center gap-2 transition shadow-sm border-none cursor-pointer hover:shadow-md hover:-translate-y-0.5"
                                            >
                                                <MessageCircle className="w-4 h-4 text-white" />
                                                <span>REQUEST QUOTE</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products — full width below the main columns */}
                {relatedProducts && relatedProducts.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-sm font-black text-[#2C3E2B]/80 tracking-wider uppercase mb-4 text-left">
                            Related Products
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {relatedProducts.map((rel, rIdx) => (
                                <ProductCard
                                    key={rIdx}
                                    product={rel}
                                    onAddToCart={handleAddToCart}
                                    isLiked={myList.includes(rel.id) || (rel.code && myList.includes(rel.code)) || (rel.title && myList.includes(rel.title))}
                                    onToggleMyList={() => handleToggleMyList(rel.id || rel.code || rel.title)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
